import React from 'react'
import { View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { paginationitemContract } from './paginationitem.contract'

const VARIANTS = paginationitemContract.props.variant.values

export type PaginationItemVariant = (typeof VARIANTS)[number]

export type PaginationItemProps = {
    variant: PaginationItemVariant
    className?: string
    testID?: string
}

const variantFor = (variant: PaginationItemVariant) => {
    const key = `variant=${variant}`
    const found = (
        paginationitemContract.variants as Record<string, { container: string; box: string }>
    )[key]
    if (!found) {
        throw new Error(`aos-ds PaginationItem: the Figma contract has no "${key}"`)
    }
    return found
}

/**
 * One page marker: a 20x10 slot with the mark drawn inside it.
 *
 * The two nodes are the point of the component. Figma gives both variants the
 * same 20x10 container and changes only what it paints inside — a 10px circle
 * for `dot`, a 20px pill for `pill` — so the row does not reflow when the
 * active page moves. AOS-1047 reports the opposite ("the container width
 * shouldn't change between page numbers"), which is the app sizing the slot to
 * the mark instead of the other way round.
 *
 * So `container` is never taken from the mark, even for `pill` where the two
 * happen to measure the same.
 */
export const PaginationItem = ({ variant, className, testID }: PaginationItemProps) => {
    const cell = variantFor(variant)
    return (
        <View
            className={twMerge('items-center justify-center', cell.container, className)}
            testID={testID ?? `pagination-${variant}`}
        >
            <View className={cell.box} testID='pagination-mark' />
        </View>
    )
}

PaginationItem.displayName = 'PaginationItem'

export { VARIANTS as PAGINATION_ITEM_VARIANTS }
